import React from "react";
import PageHeader from "../components/PageHeader";
import useScrollReveal from "../hooks/useScrollReveal";
import { contact } from "../mock";
import { Mail, Copy, Check, MapPin, Phone, Smartphone } from "lucide-react";
import { useToast } from "../hooks/use-toast";

export default function Contact() {
  useScrollReveal();
  const { toast } = useToast();
  const [copied, setCopied] = React.useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText(contact.email);
    setCopied(true);
    toast({ title: "Email copied", description: contact.email });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Get in Touch"
        title="Contact"
        subtitle="For lectures, interviews, writing commissions or correspondence, please reach out using the details below."
      />

      <section className="py-24 bg-[#faf8f4]">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#eae4d8] shadow-sm overflow-hidden reveal">
            <div className="bg-[#1a2b4a] px-8 py-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#b8935a] flex items-center justify-center text-white mx-auto">
                <Mail size={28} />
              </div>
              <h2 className="font-serif text-3xl font-bold text-white mt-6">
                {contact.name}
              </h2>
              <div className="mt-4 space-y-1">
                {contact.roles.map((r, i) => (
                  <p key={i} className="text-white/60 text-sm leading-relaxed">
                    {r}
                  </p>
                ))}
              </div>
            </div>

            <div className="p-8 md:p-10">
              <div className="grid sm:grid-cols-2 gap-6">
                <InfoRow icon={MapPin} label="Mailing Address" value={contact.address} />
                <InfoRow icon={Smartphone} label="Mobile" value={contact.mobile} />
                <InfoRow icon={Phone} label="Phone" value={contact.phone} />
                <InfoRow icon={Mail} label="Email" value={contact.email} />
              </div>

              <div className="mt-10 pt-8 border-t border-[#eae4d8] flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`mailto:${contact.email}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#b8935a] hover:bg-[#a67f45] text-white px-8 py-3.5 rounded-md font-medium transition-colors duration-300"
                >
                  <Mail size={18} /> Send an Email
                </a>
                <button
                  onClick={copyEmail}
                  className="inline-flex items-center justify-center gap-2 border border-[#d8d2c4] hover:border-[#b8935a] text-[#1a2b4a] px-8 py-3.5 rounded-md font-medium transition-colors duration-300"
                >
                  {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
                  {copied ? "Copied" : "Copy Email"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-full bg-[#f2ede2] flex items-center justify-center text-[#b8935a] flex-shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-[#8a8f9c]">{label}</p>
        <p className="text-[#1a2b4a] font-medium mt-0.5 leading-snug break-words">
          {value}
        </p>
      </div>
    </div>
  );
}
